import { useEffect, useState } from "react";

export const useSessionStorage = (
  key: string,
  initialValue: object | string,
  raw?: boolean
): any[] => {
  const [state, setState] = useState(() => {
    try {
      const sessionStorageValue = sessionStorage.getItem(key);

      if (typeof sessionStorageValue !== "string") {
        sessionStorage.setItem(
          key,
          raw ? String(initialValue) : JSON.stringify(initialValue)
        );
        return initialValue;
      } else {
        return raw
          ? sessionStorageValue
          : JSON.parse(sessionStorageValue || "null");
      }
    } catch {
      // If user is in private mode or has storage restriction
      // sessionStorage can throw. JSON.parse and JSON.stringify
      // can throw, too.
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      const serializedState = raw ? String(state) : JSON.stringify(state);
      sessionStorage.setItem(key, serializedState);

      // Dispatch a custom event so other hooks using the same key can update
      window.dispatchEvent(
        new CustomEvent("session-storage", { detail: { key, value: state } })
      );
    } catch {
      // ignore storage errors
    }
  }, [key, state, raw]);

  // Listen for storage updates emitted by other hook instances
  useEffect(() => {
    const handler = (e: any) => {
      if (e?.detail?.key === key) {
        setState(e.detail.value);
      }
    };

    window.addEventListener("session-storage", handler as any);
    return () => window.removeEventListener("session-storage", handler as any);
  }, [key]);

  return [state, setState];
};
